import { Injectable } from '@angular/core';
import { fromEvent, map, Observable, shareReplay, EMPTY, distinctUntilChanged, merge, combineLatest, startWith } from 'rxjs';

export interface IVisibilityService {
  documentVisibilityChange: Observable<boolean>;
  viewportVisibilityChange(target: Element): Observable<boolean>;
  overlayVisibilityChange(target: Element): Observable<boolean>;
  effectiveVisibilityChange(target: Element): Observable<boolean>;
}

@Injectable({
  providedIn: 'root'
})
export class VisibilityService implements IVisibilityService {
  private documentVisibility$: Observable<boolean>;
  constructor() {
    this.documentVisibility$ =
      fromEvent(document, 'visibilitychange')
        .pipe(
          map(() => document.visibilityState === 'visible'),
          startWith(document.visibilityState === 'visible'),
          shareReplay(1)
        );
  }

  get documentVisibilityChange(): Observable<boolean> {
    return this.documentVisibility$;
  }

  viewportVisibilityChange(target: Element): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            subscriber.next(entry.isIntersecting);
          });
        }
      );

      observer.observe(target);

      return () => {
        observer.unobserve(target);
        observer.disconnect();
      };
    }).pipe(
      distinctUntilChanged());
  }

  overlayVisibilityChange(target: Element): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      const checkOverlay = () => {
        const isNotCovered = !this.isElementCoveredByOverlay(target);
        subscriber.next(isNotCovered);
      };

      // Initial check
      checkOverlay();

      // Mutation Observer to detect DOM changes (modals opening/closing)
      const mutationObserver = new MutationObserver(checkOverlay);

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      return () => {
        mutationObserver.disconnect();
      };
    }).pipe(
      distinctUntilChanged());
  }

  private isElementCoveredByOverlay(target: Element): boolean {
    // Get center point of the target element
    const rect = target.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Get the topmost element at the center point
    const elementAtPoint = document.elementFromPoint(centerX, centerY);

    if (!elementAtPoint) {
      return false;
    }

    // If the element at center point is the target or inside target, it's not covered
    if (elementAtPoint === target || target.contains(elementAtPoint)) {
      return false;
    }

    // If target contains the element at point, it's not covered (nested scenario)
    if (elementAtPoint.contains(target)) {
      return false;
    }

    // Check if the covering element has higher z-index (likely an overlay)
    const targetZIndex = this.getEffectiveZIndex(target);
    const coveringZIndex = this.getEffectiveZIndex(elementAtPoint);

    return coveringZIndex > targetZIndex;
  }

  private getEffectiveZIndex(element: Element): number {
    let current = element as HTMLElement;
    let maxZIndex = 0;

    // Walk up the DOM tree to find the highest z-index in the stacking context
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const zIndex = parseInt(style.zIndex) || 0;

      if (zIndex > maxZIndex) {
        maxZIndex = zIndex;
      }

      current = current.parentElement!;
    }

    return maxZIndex;
  }

  effectiveVisibilityChange(target: Element): Observable<boolean> {
    return combineLatest([
      this.documentVisibilityChange,
      this.viewportVisibilityChange(target),
      this.overlayVisibilityChange(target)
    ]).pipe(
      map(([documentVisible, viewportVisible, notCoveredByOverlay]) => {
        // Element is effectively visible when ALL conditions are met
        return documentVisible && viewportVisible && notCoveredByOverlay;
      }),
      distinctUntilChanged()
    );
  }

}

@Injectable({
  providedIn: 'root'
})
export class NoOpVisibilityService implements IVisibilityService {
  get documentVisibilityChange(): Observable<boolean> {
    return EMPTY;
  }

  viewportVisibilityChange(target: Element): Observable<boolean> {
    return EMPTY;
  }

  overlayVisibilityChange(target: Element): Observable<boolean> {
    return EMPTY;
  }

  effectiveVisibilityChange(target: Element): Observable<boolean> {
    return EMPTY;
  }
}